import { SearchIcon } from '@heroicons/react/solid'
import Fuse from 'fuse.js'
import { useContext, useEffect, useState } from 'react'
import Test from 'react-flip-move'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import BaseButton from 'shared/components/base/button/base-button'
import BaseInput from 'shared/components/base/form/base-input'
import dictionary from 'shared/constants/dictionary'
import { SocketContext, useEmit } from 'shared/contexts/socket/socket-context'
import { setSelectedProductTypes } from 'store/slice/call-detail/call-detail.slice'
import OperatorExtension, { PhoneCallType } from 'store/slice/call-detail/types'
import { setProductTypes } from 'store/slice/crmData/crm-data.slice'
import { RootState } from 'store/store'

import NewProductRow from './new-product-row'
import ProductTypeRow from './product-type-row'
import { CategoryType } from './types/category-type'

const FlipMove: any = Test

const LeadAndSourceSubmissionForm = () => {
  const callType = useSelector(
    (state: RootState) => state.callDetail.formData.callType
  )
  const socket = useContext(SocketContext)
  const emit = useEmit()
  const dispatch = useDispatch()
  const [newProducts, setNewProducts] = useState<CategoryType[]>([])
  const { productTypes } = useSelector((state: RootState) => state.crmData)
  const [, setTeamNames] = useState<string[]>([])
  const [search, setSearch] = useState<string>('')

  function addNewProduct() {
    setNewProducts((prevState) => [
      {
        label: '',
        value: '',
        isSelected: false,
        selectedValue: '',
        id: crypto?.getRandomValues.toString(),
        team: '!new!',
      },
      ...prevState,
    ])
  }

  useEffect(() => {
    emit('get-product-types')
      .then((productTypes: { id: string; name: string; team: string }[]) => {
        dispatch(
          setProductTypes(
            productTypes.map((category) => ({
              id: category.id,
              isSelected: false,
              label: category.name,
              selectedValue: '',
              value: category.id,
              team: category.team,
            }))
          )
        )
      })
      .catch(() => {
        toast.error(dictionary.socket_timeout)
      })
  }, [])

  function populateSelectedProductTypes() {
    return [
      ...productTypes
        .filter((item) => item.isSelected)
        .map((item) => ({
          id: item.id,
          name: item.label,
          isNew: false,
          requestedQuantity: item.selectedValue,
        })),
      ...newProducts.map((item) => ({
        id: item.id,
        name: item.label,
        isNew: true,
        requestedQuantity: item.selectedValue,
      })),
    ]
  }

  useEffect(() => {
    dispatch(setSelectedProductTypes(populateSelectedProductTypes()))
  }, [productTypes, newProducts])

  function filterCategoriesBySearch(items: typeof productTypes) {
    const fuse = new Fuse(items, {
      keys: ['label'],
    })
    return fuse.search(search).map((fuseResult) => fuseResult.item)
  }

  function getCategories(): CategoryType[] {
    let result = productTypes.filter((item) => !item.isSelected)
    if (search) {
      result = filterCategoriesBySearch(result)
    }
    return [
      ...productTypes.filter((item) => item.isSelected),
      ...result.slice(0, 10),
    ]
  }

  // function getTeamNameToSelect(operators: any[], selectedProductTypes: any[]) {
  //   let foundedOperator = null
  //   let foundedTeamName = null
  //   foundedTeamName = selectedProductTypes.find((productType) =>
  //     teamNames.includes(productType.label)
  //   )?.label
  //   if (!foundedTeamName) {
  //     foundedOperator = operators.find((operator) => {
  //       if (
  //         operator.productTypes?.find((productType: any) =>
  //           selectedProductTypes.find((type) => type.label === productType)
  //         )
  //       ) {
  //         return operator
  //       }
  //       return false
  //     })
  //   }

  //   return foundedTeamName || foundedOperator?.teamName
  // }

  // function getTeamNameOfProductType(productTypes: CategoryType[]) {
  //   return productTypes[0].team
  // }

  function handleProductTypeCheck(id: string, isSelected: boolean) {
    dispatch(
      setProductTypes(
        productTypes.map((item) =>
          item.id === id ? { ...item, isSelected } : item
        )
      )
    )
  }

  function handleProductTypeSelect(id: string, selectedValue: string) {
    dispatch(
      setProductTypes(
        productTypes.map((item) =>
          item.id === id ? { ...item, selectedValue } : item
        )
      )
    )
  }

  useEffect(() => {
    socket?.on('extensionStatus', (extensions: OperatorExtension[]) => {
      if (!extensions?.length) return
      setTeamNames(() => {
        const _options: string[] = []
        const optionSet = new Set(
          extensions.map((extension) => extension.teamName)
        )
        optionSet.forEach((item) => _options.push(item))
        return _options
      })
    })
  }, [])

  return (
    <>
      <div className="mb-2 font-medium text-3.25 font-normal text-[#0086B3]">
        {callType === PhoneCallType.SOURCE
          ? 'دسته بندی کالا'
          : 'کالای درخواستی'}
      </div>
      <div className="grid grid-cols-11 gap-4 justify-content items-center">
        <BaseInput
          className="mb-4 col-span-7"
          trailingIcon={SearchIcon}
          value={search}
          onChange={(event) => {
            if (event.target.value.includes('*')) {
              event.target.value = event.target.value.replace('*', '')
              document.getElementById('search-operator')?.focus()
              return
            }
            setSearch(event.target.value)
          }}
        />
        <BaseButton
          className="font-medium text-3.25 leading-7.5 text-gray-medium col-span-4 mb-4 text-right px-0 pl-4 py-0 w-fit"
          onClick={addNewProduct}
        >
          + کالای جدید
        </BaseButton>
      </div>
      <div className="lg:h-[15.5rem] xl:h-[21rem] overflow-y-auto soft-scrollbar">
        <FlipMove className="flex flex-col space-y-3 mb-3">
          {newProducts.map((newProduct) => (
            <NewProductRow
              hasSelectComponent={callType === PhoneCallType.LEAD}
              newCategory={newProduct}
              key={newProduct.id}
              onRemove={(id) => {
                setNewProducts((prevState) => {
                  const _state = [...prevState]
                  const index = _state.findIndex((item) => item.id === id)
                  _state.splice(index, 1)
                  return _state
                })
              }}
              onNameChange={(id, name) => {
                setNewProducts((prevState) => {
                  const _state = [...prevState]
                  const index = _state.findIndex((item) => item.id === id)
                  _state[index].value = name
                  _state[index].label = name
                  return _state
                })
              }}
              onRequestedQuantitySelect={(id, selectedValue) => {
                setNewProducts((prevState) => {
                  const _state = [...prevState]
                  const index = _state.findIndex((item) => item.id === id)
                  _state[index].selectedValue = selectedValue
                  return _state
                })
              }}
            />
          ))}
        </FlipMove>

        <FlipMove className="flex flex-col space-y-3 mb-3">
          {getCategories()?.map((category) => (
            <ProductTypeRow
              hasSelectComponent={callType === PhoneCallType.LEAD}
              key={category.id}
              onSelect={handleProductTypeSelect}
              category={category}
              onCheck={handleProductTypeCheck}
            />
          ))}
        </FlipMove>
      </div>
    </>
  )
}

export default LeadAndSourceSubmissionForm
